import React, { useState, useEffect } from 'react';
import { X, Filter, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';

const AgentStatusFilterModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  selectedStatuses = [],
  statusOptions = [
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800 border-green-300' },
    { value: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-800 border-gray-300' },
    { value: 'busy', label: 'Busy', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { value: 'away', label: 'Away', color: 'bg-red-100 text-red-800 border-red-300' },
    { value: 'pending', label: 'Pending', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { value: 'suspended', label: 'Suspended', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  ]
}) => {
  const [localSelectedStatuses, setLocalSelectedStatuses] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedStatuses([...selectedStatuses]);
    }
  }, [isOpen, selectedStatuses]);

  if (!isOpen) return null;

  const handleToggleStatus = (statusValue) => {
    setLocalSelectedStatuses(prev => {
      if (prev.includes(statusValue)) {
        return prev.filter(s => s !== statusValue);
      } else {
        return [...prev, statusValue];
      }
    });
  };

  const handleSelectAll = () => {
    if (localSelectedStatuses.length === statusOptions.length) {
      setLocalSelectedStatuses([]);
    } else {
      setLocalSelectedStatuses(statusOptions.map(opt => opt.value));
    }
  };

  const handleClearAll = () => {
    setLocalSelectedStatuses([]);
  };

  const handleApply = () => {
    onApply(localSelectedStatuses);
    onClose();
  };

  const handleReset = () => {
    setLocalSelectedStatuses([...selectedStatuses]);
  };

  const allSelected = localSelectedStatuses.length === statusOptions.length;
  const noneSelected = localSelectedStatuses.length === 0;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Filter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Filter by Status
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select agent statuses to filter
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Quick Actions */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="text-sm"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700"
              disabled={noneSelected}
            >
              Clear All
            </Button>
          </div>

          {/* Status Options */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {statusOptions.map((status) => {
              const isSelected = localSelectedStatuses.includes(status.value);
              
              return (
                <div
                  key={status.value}
                  className={`
                    flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                  onClick={() => handleToggleStatus(status.value)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleStatus(status.value)}
                      className="pointer-events-none"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm px-2 py-1 rounded border font-medium capitalize">
                        {status.label}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="ml-2">
                      <Check className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Count */}
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {localSelectedStatuses.length === 0 ? (
                'No statuses selected'
              ) : localSelectedStatuses.length === statusOptions.length ? (
                'All statuses selected'
              ) : (
                `${localSelectedStatuses.length} of ${statusOptions.length} statuses selected`
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={JSON.stringify(localSelectedStatuses) === JSON.stringify(selectedStatuses)}
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            disabled={localSelectedStatuses.length === 0}
          >
            Apply Filter
            {localSelectedStatuses.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {localSelectedStatuses.length}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgentStatusFilterModal;

